/**
 * Helpers shared by the TANUH upload UIs (onboarding "Upload files" step and the Manage Files
 * modal) for folder uploads: turning dropped folders into files with relative paths, and turning
 * a list of paths into a flat, collapsible tree.
 */

export interface DroppedFile {
  file: File;
  /** Path relative to the dropped root, e.g. "images/cats/1.png". */
  path: string;
}

/** Recursively reads dropped files/folders (from `webkitGetAsEntry`), keeping relative paths. */
export async function readDroppedEntries(entries: FileSystemEntry[]): Promise<DroppedFile[]> {
  const out: DroppedFile[] = [];
  const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (entry as FileSystemFileEntry).file(resolve, reject));
      out.push({ file, path: prefix + file.name });
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      // readEntries returns results in batches; keep reading until empty
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
          reader.readEntries(resolve, reject));
        if (batch.length === 0) break;
        for (const child of batch) {
          await walk(child, prefix + entry.name + '/');
        }
      }
    }
  };
  for (const entry of entries) {
    await walk(entry, '');
  }
  return out;
}

/** Folder keys ("a", "a/b", …) that exist for the given file paths. */
export function collectFolderKeys(paths: string[]): string[] {
  const keys = new Set<string>();
  for (const path of paths) {
    const parts = path.split('/');
    for (let i = 1; i < parts.length; i++) {
      keys.add(parts.slice(0, i).join('/'));
    }
  }
  return Array.from(keys);
}

export type FileTreeRow<S, U> =
  | { kind: 'folder'; key: string; name: string; depth: number; count: number; collapsed: boolean }
  | { kind: 'selected'; key: string; name: string; depth: number; ref: S }
  | { kind: 'uploaded'; key: string; name: string; depth: number; ref: U };

interface FileTreeNode<S, U> {
  folders: Map<string, FileTreeNode<S, U>>;
  files: Array<{ name: string; selected?: S; uploaded?: U }>;
  count: number;
}

/** Flattened, currently-visible rows: folders first (A–Z), then files (A–Z); collapsed folders hide their children. */
export function buildFileTreeRows<S, U>(
  selected: Array<{ path: string; ref: S }>,
  uploaded: Array<{ path: string; ref: U }>,
  collapsed: Set<string>,
): Array<FileTreeRow<S, U>> {
  const root: FileTreeNode<S, U> = { folders: new Map(), files: [], count: 0 };
  const insert = (path: string, entry: { selected?: S; uploaded?: U }) => {
    const parts = path.split('/').filter((p) => p.length > 0);
    const name = parts.pop() ?? path;
    let node = root;
    node.count++;
    for (const part of parts) {
      let child = node.folders.get(part);
      if (!child) {
        child = { folders: new Map(), files: [], count: 0 };
        node.folders.set(part, child);
      }
      child.count++;
      node = child;
    }
    node.files.push({ name, ...entry });
  };
  uploaded.forEach((u) => insert(u.path, { uploaded: u.ref }));
  selected.forEach((s) => insert(s.path, { selected: s.ref }));

  const rows: Array<FileTreeRow<S, U>> = [];
  const flatten = (node: FileTreeNode<S, U>, prefix: string, depth: number) => {
    const folderNames = Array.from(node.folders.keys()).sort((a, b) => a.localeCompare(b));
    for (const name of folderNames) {
      const child = node.folders.get(name)!;
      const key = prefix + name;
      const isCollapsed = collapsed.has(key);
      rows.push({ kind: 'folder', key, name, depth, count: child.count, collapsed: isCollapsed });
      if (!isCollapsed) {
        flatten(child, key + '/', depth + 1);
      }
    }
    const files = [...node.files].sort((a, b) => a.name.localeCompare(b.name));
    for (const f of files) {
      const key = prefix + f.name;
      if (f.uploaded !== undefined) {
        rows.push({ kind: 'uploaded', key, name: f.name, depth, ref: f.uploaded });
      } else if (f.selected !== undefined) {
        rows.push({ kind: 'selected', key, name: f.name, depth, ref: f.selected });
      }
    }
  };
  flatten(root, '', 0);
  return rows;
}
